import React from 'react';
import { Button, Descriptions, Space } from 'antd';
import Vehicle from '../../../core/models/Vehicle';
import { useSelector } from 'react-redux';
import {
  getVehicleMakes,
  getVehicleModels,
  getVehicleGrades,
  getVehicleColors,
  getVehicleTypes,
  getVehicleConditions,
  getVehicleCylinders,
  getFuelTypes,
  getTransmissions,
  getVehicleCategories,
  getVehicleLocations,
} from '../../../app/store/commonSlice';
import { formatDate } from '../../../utils/dateTimeUtil';

interface IProps {
  vehicle: Vehicle | null;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
  layout?: 'horizontal' | 'vertical';
}

const ViewVehicleDetails: React.FC<IProps> = ({
  vehicle,
  onEditClick,
  onDeleteClick,
  layout = 'horizontal',
}: IProps) => {
  const vehicleMakes = useSelector(getVehicleMakes);
  const vehicleModels = useSelector(getVehicleModels);
  const vehicleGrades = useSelector(getVehicleGrades);
  const vehicleColors = useSelector(getVehicleColors);
  const vehicleTypes = useSelector(getVehicleTypes);
  const vehicleConditions = useSelector(getVehicleConditions);
  const vehicleCylinders = useSelector(getVehicleCylinders);
  const vehicleLocations = useSelector(getVehicleLocations);
  const vehicleCategories = useSelector(getVehicleCategories);
  const fuelTypes = useSelector(getFuelTypes);
  const transmissions = useSelector(getTransmissions);

  return (
    <Descriptions
      bordered
      layout={layout}
      labelStyle={{ fontWeight: 'bold' }}
      colon={false}
      size='small'
      extra={
        onEditClick && (
          <Space>
            <Button type='primary' danger ghost onClick={onDeleteClick}>
              Delete
            </Button>
            <Button type='primary' ghost onClick={onEditClick}>
              Edit
            </Button>
          </Space>
        )
      }
    >
      <Descriptions.Item label='VID'>{vehicle?.code}</Descriptions.Item>
      <Descriptions.Item label='Registration No.'>{vehicle?.plateNumber}</Descriptions.Item>
      <Descriptions.Item label='Chassis No.'>{vehicle?.chassisNo}</Descriptions.Item>
      <Descriptions.Item label='Engine No.'>{vehicle?.engineNumber}</Descriptions.Item>
      <Descriptions.Item label='Make'>
        {vehicleMakes?.find((make) => make.id === vehicle?.make)?.name}
      </Descriptions.Item>
      <Descriptions.Item label='Model'>
        {vehicleModels?.find((model) => model.id === vehicle?.model)?.name}
      </Descriptions.Item>
      <Descriptions.Item label='CC'>
        {vehicleCylinders?.find((cylinder) => cylinder.id === vehicle?.cylinder)?.name}
      </Descriptions.Item>
      <Descriptions.Item label='Grade'>
        {vehicleGrades?.find((grade) => grade.id === vehicle?.grade)?.name}
      </Descriptions.Item>
      <Descriptions.Item label='Fuel Type'>
        {fuelTypes?.find((fuelType) => fuelType.id === vehicle?.fuelType)?.name}
      </Descriptions.Item>
      <Descriptions.Item label='Type of Body'>
        {vehicleTypes?.find((type) => type.id === vehicle?.type)?.type}
      </Descriptions.Item>
      <Descriptions.Item label='Year of Manufacture'>{vehicle?.yom}</Descriptions.Item>
      <Descriptions.Item label='Colour'>
        {vehicleColors?.find((color) => color.id === vehicle?.color)?.name}
      </Descriptions.Item>
      <Descriptions.Item label='No of Seats (Ex Driver)'>{vehicle?.noSeats}</Descriptions.Item>
      <Descriptions.Item label='Condition of Vehicle'>
        {vehicleConditions?.find((condition) => condition.id === vehicle?.condition)?.condition ??
          'N/A'}
      </Descriptions.Item>
      <Descriptions.Item label='Mileage'>
        {vehicle?.mileage?.toLocaleString() ?? 'N/A'}
      </Descriptions.Item>
      <Descriptions.Item label='Transmission'>
        {transmissions?.find((transmission) => transmission.id === vehicle?.transmissionType)
          ?.name ?? 'N/A'}
      </Descriptions.Item>
      <Descriptions.Item label='No of Doors'>{vehicle?.noDoors ?? 'N/A'}</Descriptions.Item>
      <Descriptions.Item label='Drivetrain(2WD/4WD)'>
        {vehicle?.drivetrain ?? 'N/A'}
      </Descriptions.Item>
      <Descriptions.Item label='Vehicle Category'>
        {vehicleCategories?.find((category) => category.id === vehicle?.category)?.category ??
          'N/A'}
      </Descriptions.Item>
      <Descriptions.Item label='Location'>
        {vehicleLocations?.find((location) => location.id === vehicle?.location)?.name}
      </Descriptions.Item>
      <Descriptions.Item label='Expiry Revenue License'>
        {formatDate(vehicle?.revenueLicenseExpDate)}
      </Descriptions.Item>
      <Descriptions.Item label='Expiry Insurance'>
        {formatDate(vehicle?.expiryInsuranceDate)}
      </Descriptions.Item>
    </Descriptions>
  );
};

export default ViewVehicleDetails;
